/**
 * Чтение ответа ассистента: ручной `fetch` и разбор NDJSON по строкам.
 *
 * Мимо сгенерированного клиента и мимо axios, потому что ни тот, ни другой не
 * отдают тело по мере прихода: openapi-generator ждёт целый JSON, а axios в
 * браузере — весь ответ. Ответ ассистента пишется секундами, и панель обязана
 * показывать его по ходу, а не после.
 *
 * `EventSource` не годится: он не умеет заголовок `Authorization`, а токен в
 * адресе осел бы в логах nginx и в Referer. Вебсокет не нужен — говорит только
 * сервер.
 *
 * Формат — по одной JSON-строке на событие. Чанк рвётся где угодно, в том
 * числе посреди строки, поэтому хвост копится в буфере до следующего `\n`.
 */
import { dropSession, ensureFreshToken } from '@/api/http'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

/** Виды событий потока; повторяют `assistantEvent.Type` бэкенда (DEV-1553). */
export type AssistantEventType =
  | 'quota'
  | 'text'
  | 'tool_call'
  | 'tool_result'
  | 'draft_action'
  | 'done'

/** Чем кончился ход. `error` приезжает, когда статус ответа уже отдан. */
export type AssistantDoneReason =
  | 'stop'
  | 'tool_limit'
  | 'awaiting_confirmation'
  | 'cancelled'
  | 'error'

/** Остаток дневного лимита; `limit: 0` — лимита нет. */
export interface AssistantQuota {
  used: number
  limit: number
  remaining: number
}

/**
 * Событие потока — зеркало структуры бэкенда: поля приходят по одному на вид
 * события, поэтому здесь они необязательные, а не разложены по union-типам.
 * Разбирать союз пришлось бы каждому потребителю, а полей и так меньше десятка.
 */
export interface AssistantEvent {
  type: AssistantEventType
  text?: string
  call_id?: string
  name?: string
  args?: unknown
  result?: unknown
  error_type?: string
  message?: string
  message_id?: number
  reason?: AssistantDoneReason
  duration_ms?: number
  quota?: AssistantQuota
}

/** Ошибка до первой строки потока: у неё ещё есть код ответа. */
export class AssistantHttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'AssistantHttpError'
  }
}

export const threadPath = (threadId: string) =>
  `${baseURL}/api/v1/tracker/assistant/threads/${threadId}`

/**
 * Разбирает поток NDJSON и зовёт `onEvent` на каждую целую строку.
 *
 * Отдельная функция, а не кусок `readStream`, потому что именно здесь живёт
 * граница чанков — единственное место, которое ломается тихо: битая строка не
 * даёт ошибки, она просто теряет событие.
 */
export const readNdjson = async (
  body: ReadableStream<Uint8Array>,
  onEvent: (event: AssistantEvent) => void,
): Promise<void> => {
  const reader = body.getReader()
  const decoder = new TextDecoder()

  let rest = ''

  const flush = (chunk: string) => {
    rest += chunk

    const lines = rest.split('\n')
    // Последний кусок — либо хвост незакрытой строки, либо пустая строка
    // после `\n`. В обоих случаях он ждёт следующего чанка.
    rest = lines.pop() ?? ''

    for (const line of lines) {
      if (!line.trim()) continue

      try {
        onEvent(JSON.parse(line) as AssistantEvent)
      } catch {
        // Строка не разобралась — событие потеряно, но поток жив: обрывать
        // ответ целиком из-за одной битой строки хуже, чем пропустить её.
      }
    }
  }

  for (;;) {
    const { done, value } = await reader.read()

    if (done) break

    flush(decoder.decode(value, { stream: true }))
  }

  // Хвост без завершающего `\n`: сервер закрыл соединение сразу за последним
  // событием, и без этого прогона терялось бы именно `done`.
  flush(decoder.decode())

  if (rest.trim()) {
    try {
      onEvent(JSON.parse(rest) as AssistantEvent)
    } catch {
      // см. выше
    }
  }
}

/** Что сказать человеку, когда ответ не начался. */
const errorMessageOf = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = (await response.json()) as { message?: string }

    return body.message || fallback
  } catch {
    return fallback
  }
}

interface TurnOptions {
  /** Тело запроса: вопрос человека или решение по черновику */
  body: unknown
  signal: AbortSignal
  onEvent: (event: AssistantEvent) => void
  fallbackError: string
}

/**
 * Ход ассистента: POST и чтение потока до конца.
 *
 * 401 повторяется ровно один раз и через общий `ensureFreshToken` — свой
 * refresh здесь завёл бы вторую гонку за ротацию токена с axios-интерцептором
 * (DEV-1574).
 */
export const streamTurn = async (url: string, options: TurnOptions): Promise<void> => {
  const send = async (token: string) =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(options.body),
      signal: options.signal,
    })

  let response = await send(await ensureFreshToken())

  if (response.status === 401) {
    // Токен в хранилище протух — обновляем принудительно и пробуем ещё раз.
    let refreshed = ''

    try {
      refreshed = await ensureFreshToken(true)
    } catch {
      dropSession()

      throw new AssistantHttpError(401, options.fallbackError)
    }

    response = await send(refreshed)

    if (response.status === 401) {
      dropSession()

      throw new AssistantHttpError(401, options.fallbackError)
    }
  }

  if (!response.ok || !response.body) {
    throw new AssistantHttpError(
      response.status,
      await errorMessageOf(response, options.fallbackError),
    )
  }

  await readNdjson(response.body, options.onEvent)
}
